import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.REACT_APP_SUPABASE_URL!;
const key = import.meta.env.REACT_APP_SUPABASE_ANON_KEY!;

if (!url || !key) {
  console.error('❌ [Supabase] Missing credentials:', { url: !!url, key: !!key });
} else {
  console.log('✅ [Supabase] Credentials loaded:', { url: url.substring(0, 30) + '...', keyLength: key.length });
}

export const supabase = createClient(url, key, {
  db: { schema: 'api' },
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
});

// Client for public schema tables (campaigns_genialeads, etc.)
// Share same auth session, no double persistence
export const supabasePublic = createClient(url, key, {
  db: { schema: 'api' },
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    storageKey: 'sb-public-client',
  },
});

// Dedicated client for tables that live in the public Postgres schema
// (interview_slots primarily — webhook reads/writes there, frontend must align)
export const supabasePublicSchema = createClient(url, key, {
  db: { schema: 'public' },
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    storageKey: 'sb-public-schema-client',
  },
});

export const EDGE_FUNCTIONS_URL: string | null = url
  ? `${url}/functions/v1`
  : null;

export const SUPABASE_ANON_KEY: string = key || '';
export const SUPABASE_EDGE_URL: string = url ? url.replace('/rest/v1', '') : '';

export const testSupabaseConnection = async (): Promise<{ ok: boolean; time: number; error?: string }> => {
  const t0 = performance.now();
  try {
    const { count, error } = await supabase
      .schema('api').from('job_descriptions')
      .select('count', { count: 'exact' })
      .limit(0);
    const time = performance.now() - t0;
    if (error) {
      console.warn('[Supabase] Connection test failed:', error);
      return { ok: false, time, error: error.message };
    }
    console.log(`[Supabase] Connection test OK in ${time.toFixed(0)}ms`);
    return { ok: true, time };
  } catch (err: any) {
    const time = performance.now() - t0;
    console.error('[Supabase] Connection test exception:', err);
    return { ok: false, time, error: err.message };
  }
};

export const healthCheck = async (): Promise<{
  ok: boolean;
  time: number;
  code?: string;
  message?: string;
}> => {
  const t0 = performance.now();
  console.log('[Supabase] Health check starting...');

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const res = await fetch(`${SUPABASE_EDGE_URL}/rest/v1/`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    const time = performance.now() - t0;

    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        return { ok: false, time, code: 'ERR_AUTH_FAILED', message: 'Invalid API key or auth failed' };
      }
      if (res.status >= 500) {
        return { ok: false, time, code: 'ERR_SUPABASE_UNREACHABLE', message: 'Supabase server error' };
      }
      return { ok: false, time, code: 'ERR_NETWORK', message: `HTTP ${res.status}` };
    }

    console.log(`[Supabase] Health check OK in ${time.toFixed(0)}ms`);
    return { ok: true, time };
  } catch (err: any) {
    const time = performance.now() - t0;
    const message = err?.message || 'Unknown error';
    console.log('[Supabase] Health check error:', { name: err.name, message, time: `${time.toFixed(0)}ms` });

    if (err.name === 'AbortError' || message.includes('timeout') || message.includes('Timeout')) {
      return { ok: false, time, code: 'ERR_SUPABASE_UNREACHABLE', message: 'Request timeout - Supabase unreachable' };
    }
    if (message.includes('CORS') || message.includes('cors') || message.includes('Failed to fetch')) {
      return { ok: false, time, code: 'ERR_CORS_BLOCKED', message: 'CORS policy blocked request' };
    }
    if (message.includes('NetworkError')) {
      return { ok: false, time, code: 'ERR_NETWORK', message: 'Network error - check connection' };
    }

    console.error('[Supabase] Health check failed:', err);
    return { ok: false, time, code: 'ERR_NETWORK', message };
  }
};
